import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';

const pad = (n) => String(n).padStart(2, '0');
const clampH = (h) => Math.min(23, Math.max(0, h));
const clampM = (m) => Math.min(59, Math.max(0, m));

const parse = (value) => {
  if (!value) return { h: '', m: '' };
  const [H, M] = String(value).split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(H)) return { h: '', m: '' };
  return { h: pad(clampH(H)), m: pad(clampM(Number.isNaN(M) ? 0 : M)) };
};

export function toTime24(text) {
  if (text === null || text === undefined) return text;
  const s = String(text);
  const m = s.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?/);
  if (!m) return s;
  let H = parseInt(m[1], 10);
  const M = parseInt(m[2], 10);
  const ap = m[3] ? m[3].toUpperCase() : '';
  if (ap === 'PM' && H !== 12) H += 12;
  if (ap === 'AM' && H === 12) H = 0;
  if (H > 23 || M > 59) return s;
  return `${pad(H)}:${pad(M)}`;
}

/* ── Clock popover ───────────────────────────────────────────────────────── */
const POP_W = 240;
const POP_H = 352;

function Clock24({ hour, minute, onApply, onClose }) {
  const [mode, setMode] = useState('hour'); // 'hour' | 'minute'
  const [h, setH] = useState(hour);
  const [m, setM] = useState(minute);
  const boxRef = useRef(null);

  useEffect(() => { boxRef.current?.focus(); }, []);

  const adjust = (dir, big) => {
    if (mode === 'hour') setH((x) => (x + dir + 24) % 24);
    else setM((x) => (x + dir * (big ? 5 : 1) + 60) % 60);
  };

  const onKey = (e) => {
    const k = e.key;
    if (k === 'ArrowUp' || k === 'ArrowRight') { e.preventDefault(); adjust(+1, e.shiftKey); }
    else if (k === 'ArrowDown' || k === 'ArrowLeft') { e.preventDefault(); adjust(-1, e.shiftKey); }
    else if (k === 'Enter') {
      e.preventDefault();
      if (mode === 'hour') setMode('minute'); else onApply(h, m);
    }
    else if (k === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
    else if (k === 'Tab') { e.preventDefault(); setMode((md) => (md === 'hour' ? 'minute' : 'hour')); }
    else if (k === 'h' || k === 'H') { e.preventDefault(); setMode('hour'); }
    else if (k === 'm' || k === 'M') { e.preventDefault(); setMode('minute'); }
  };

  const pt = (deg, r) => {
    const a = (deg * Math.PI) / 180;
    return { x: 100 + r * Math.sin(a), y: 100 - r * Math.cos(a) };
  };

  // Hours: 00–11 on the outer ring, 12–23 on the inner ring.
  const OUTER = 78;
  const INNER = 50;
  const handDeg = mode === 'hour' ? (h % 12) * 30 : m * 6;
  const handR = mode === 'hour' ? (h >= 12 ? INNER : OUTER) : OUTER;
  const handEnd = pt(handDeg, handR - 14);
  const tip = pt(handDeg, handR);
  const PRIMARY = 'rgb(var(--color-primary-600))';

  const pick = (val) => {
    if (mode === 'hour') { setH(val); setMode('minute'); } else setM(val);
  };

  const marks = mode === 'hour'
    ? [
        ...Array.from({ length: 12 }, (_, i) => ({ val: i, r: OUTER, deg: i * 30, size: 13 })),
        ...Array.from({ length: 12 }, (_, i) => ({ val: i + 12, r: INNER, deg: i * 30, size: 11 })),
      ]
    : Array.from({ length: 12 }, (_, i) => ({ val: i * 5, r: OUTER, deg: i * 30, size: 13 }));

  const segCls = (active) =>
    `px-1.5 rounded text-2xl font-semibold tabular-nums ${active ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`;

  return (
    <div
      ref={boxRef}
      tabIndex={-1}
      onKeyDown={onKey}
      style={{ width: POP_W }}
      className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg outline-none"
    >
      {/* readout */}
      <div className="mb-2 flex items-center justify-center gap-1">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setMode('hour')} className={segCls(mode === 'hour')}>{pad(h)}</button>
        <span className="text-2xl font-semibold text-gray-400">:</span>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setMode('minute')} className={segCls(mode === 'minute')}>{pad(m)}</button>
      </div>

      {/* dial */}
      <svg viewBox="0 0 200 200" className="mx-auto block h-[200px] w-[200px] select-none">
        <circle cx="100" cy="100" r="96" fill="#f3f4f6" />
        <line x1="100" y1="100" x2={handEnd.x} y2={handEnd.y} stroke={PRIMARY} strokeWidth="2" />
        <circle cx="100" cy="100" r="3" style={{ fill: PRIMARY }} />
        {/* small dot for in-between minutes (not on a labelled number) */}
        {mode === 'minute' && m % 5 !== 0 && <circle cx={tip.x} cy={tip.y} r="5" style={{ fill: PRIMARY }} />}
        {marks.map(({ val, r, deg, size }) => {
          const selected = mode === 'hour' ? h === val : m === val;
          const c = pt(deg, r);
          return (
            <g
              key={`${mode}-${val}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(val)}
              style={{ cursor: 'pointer' }}
            >
              <circle cx={c.x} cy={c.y} r={r === INNER ? 12 : 15} style={{ fill: selected ? PRIMARY : 'transparent' }} />
              <text
                x={c.x}
                y={c.y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={size}
                fontWeight="600"
                fill={selected ? '#fff' : r === INNER ? '#6b7280' : '#374151'}
              >
                {pad(val)}
              </text>
            </g>
          );
        })}
      </svg>

      <p className="mt-1 text-center text-[10px] leading-snug text-gray-400">
        ↑↓ adjust · Shift+↑↓ ±5 min · Tab hour/min<br />Enter next / OK · Esc cancel
      </p>
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded px-2.5 py-1 text-[11px] font-semibold text-gray-500 hover:bg-gray-100">Cancel</button>
        <button type="button" onClick={() => onApply(h, m)} className="rounded bg-primary-600 px-3 py-1 text-[11px] font-semibold text-white hover:bg-primary-700">OK</button>
      </div>
    </div>
  );
}

/* ── Field ───────────────────────────────────────────────────────────────── */
export default function TimeField24({
  value,
  onChange,
  label,
  required = false,
  hasError = false,
  disabled = false,
  fieldClassName = 'h-9 rounded-lg',
}) {
  const parsed = parse(value);
  const [hourText, setHourText] = useState(parsed.h);
  const [minText, setMinText] = useState(parsed.m);

  const hourRef = useRef(null);
  const minRef = useRef(null);
  const boxRef = useRef(null);
  const popRef = useRef(null);
  const [clockOpen, setClockOpen] = useState(false);
  const [popPos, setPopPos] = useState({ top: 0, left: 0 });

  // Follow the value when it changes from outside (reset, auto-fill, …).
  useEffect(() => { setHourText(parsed.h); }, [parsed.h]);
  useEffect(() => { setMinText(parsed.m); }, [parsed.m]);

  // Close the clock on an outside click, a scroll outside it, or a resize.
  useEffect(() => {
    if (!clockOpen) return undefined;
    const onDown = (e) => {
      if (boxRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setClockOpen(false);
    };
    const onScroll = (e) => { if (!popRef.current?.contains(e.target)) setClockOpen(false); };
    const onResize = () => setClockOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [clockOpen]);

  // Commit only when both parts are filled; never wipe mid-typing.
  const commit = (h, m) => {
    if (h === '' && m === '') { onChange(''); return; }
    if (h === '' || m === '') return;
    const H = parseInt(h, 10);
    const M = parseInt(m, 10);
    if (Number.isNaN(H) || Number.isNaN(M)) return;
    onChange(`${pad(clampH(H))}:${pad(clampM(M))}`);
  };

  const handleHourChange = (e) => {
    let raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (raw.length === 2 && parseInt(raw, 10) > 23) raw = '23';
    setHourText(raw);

    const complete = raw.length === 2 || (raw.length === 1 && parseInt(raw, 10) >= 3);
    if (complete) {
      if (minText !== '') commit(raw, minText);
      minRef.current?.focus();
    }
  };

  const handleMinChange = (e) => {
    let raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (raw.length === 2 && parseInt(raw, 10) > 59) raw = '59';
    setMinText(raw);
    if (raw.length === 2) commit(hourText, raw);
  };


  const padOnBlur = (which) => (e) => {
    const h = which === 'h' ? e.target.value : (hourRef.current?.value ?? hourText);
    const m = which === 'm' ? e.target.value : (minRef.current?.value ?? minText);
    if (which === 'h' && h.length === 1) {
      const v = pad(h);
      setHourText(v);
      commit(v, m);
      return;
    }
    if (which === 'm' && m.length === 1) {
      const v = pad(m);
      setMinText(v);
      commit(h, v);
      return;
    }
    if (h === '' && m === '') onChange('');
  };

  const step = (which, dir, big = false) => {
    if (which === 'h') {
      const cur = hourText === '' ? new Date().getHours() : parseInt(hourText, 10) || 0;
      const next = pad((cur + dir + 24) % 24);
      setHourText(next);
      commit(next, minText || '00');
    } else {
      const cur = parseInt(minText, 10) || 0;
      const next = pad((cur + dir * (big ? 5 : 1) + 60) % 60);
      setMinText(next);
      commit(hourText || pad(new Date().getHours()), next);
    }
  };

  const openClock = () => {
    if (disabled) return;
    const r = boxRef.current?.getBoundingClientRect();
    if (r) {
      const below = r.bottom + 4;
      const top = below + POP_H > window.innerHeight ? Math.max(8, r.top - POP_H - 4) : below;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - POP_W - 8));
      setPopPos({ top, left });
    }
    setClockOpen(true);
  };

  const handleKeyDown = (which) => (e) => {
    if ((e.altKey && e.key === 'ArrowDown') || e.key === 'F4') { e.preventDefault(); openClock(); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); step(which, +1, e.shiftKey); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); step(which, -1, e.shiftKey); }
    else if (e.key === 'ArrowRight' && which === 'h') { e.preventDefault(); minRef.current?.focus(); }
    else if (e.key === 'ArrowLeft' && which === 'm') { e.preventDefault(); hourRef.current?.focus(); }
    else if ((e.key === ':' || e.key === 'Enter') && which === 'h') { e.preventDefault(); minRef.current?.focus(); }
    else if ((e.key === 'Backspace' || e.key === 'Delete') && which === 'm' && minText === '') {
      e.preventDefault(); hourRef.current?.focus();
    }
  };

  const applyClock = (h, m) => {
    setHourText(pad(h));
    setMinText(pad(m));
    commit(pad(h), pad(m));
    setClockOpen(false);
    setTimeout(() => hourRef.current?.focus(), 0);
  };

  const closeClock = () => {
    setClockOpen(false);
    setTimeout(() => hourRef.current?.focus(), 0);
  };

  const now = new Date();
  const borderCls = hasError
    ? 'border-red-400 focus-within:border-red-500'
    : 'border-gray-200 focus-within:border-primary-500';

  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-xs font-medium text-gray-700">
          {label} {required && '*'}
        </label>
      )}
      <div
        ref={boxRef}
        className={`inline-flex items-center border text-xs overflow-hidden ${fieldClassName} ${borderCls} ${
          disabled ? 'bg-gray-50 text-gray-400' : 'bg-white text-gray-700'
        }`}
      >
        <input
          type="text"
          inputMode="numeric"
          value={hourText}
          onChange={handleHourChange}
          onKeyDown={handleKeyDown('h')}
          onFocus={(e) => e.target.select()}
          onBlur={padOnBlur('h')}
          placeholder="HH"
          disabled={disabled}
          className="w-8 text-center outline-none bg-transparent tabular-nums"
          ref={hourRef}
          aria-label="Hour (00–23)"
        />
        <span className="text-gray-400 select-none">:</span>
        <input
          type="text"
          inputMode="numeric"
          value={minText}
          onChange={handleMinChange}
          onKeyDown={handleKeyDown('m')}
          onFocus={(e) => e.target.select()}
          onBlur={padOnBlur('m')}
          placeholder="MM"
          disabled={disabled}
          className="w-8 text-center outline-none bg-transparent tabular-nums"
          ref={minRef}
          aria-label="Minute"
        />
        <button
          type="button"
          onClick={() => (clockOpen ? setClockOpen(false) : openClock())}
          disabled={disabled}
          title="Open clock (Alt+↓)"
          aria-label="Open clock"
          className="ml-0.5 flex h-full items-center border-l border-gray-200 px-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-primary-600"
        >
          <Clock size={13} />
        </button>
      </div>

      {clockOpen && createPortal(
        <div ref={popRef} style={{ position: 'fixed', top: popPos.top, left: popPos.left, zIndex: 10000 }}>
          <Clock24
            hour={hourText !== '' ? clampH(parseInt(hourText, 10) || 0) : now.getHours()}
            minute={minText !== '' ? clampM(parseInt(minText, 10) || 0) : now.getMinutes()}
            onApply={applyClock}
            onClose={closeClock}
          />
        </div>,
        document.body
      )}
    </div>
  );
}